import '../styles/NewAthletePage.css';

import React, {
  useRef,
  useState,
} from 'react';

import {
  doc,
  getFirestore,
  updateDoc,
} from 'firebase/firestore';
import { useParams } from 'react-router-dom';

import firebase from '../firebase';
import { Athlete } from '../types/types';
import {
  Button,
  Card,
  Container,
  SkillJudge,
  Typography,
} from './common';
import CommentsBoxReadonly from './common/CommentsBoxReadonly';

interface AthleteDetailPageFinalResultProps {
  athlete: Athlete;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  hasNext: boolean;
  hasPrevious: boolean;
  onRefresh?: () => void;
}

export default function AthleteDetailPageFinalResult({
  athlete,
  onClose,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
  onRefresh
}: AthleteDetailPageFinalResultProps) {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showFullImage, setShowFullImage] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedSkillImage, setSelectedSkillImage] = useState<string | null>(null);
  const [fullImageLoaded, setFullImageLoaded] = useState(false);
  
  const db = getFirestore(firebase);

  // Ref for scrolling to top
  const cardRef = useRef<HTMLDivElement>(null);

  // Handlers that scroll the .athlete-detail-content div to top and then call the original handler
  const handleNext = () => {
    if (cardRef.current) {
      cardRef.current.scrollTop = 0;
    }
    onNext();
  };
  const handlePrevious = () => {
    if (cardRef.current) {
      cardRef.current.scrollTop = 0;
    }
    onPrevious();
  };

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  const renderSkillJudge = (title: string, value: number | undefined) => {
    if (value === undefined || value === 0) return null;
    
    return (
      <SkillJudge 
        title={title} 
        value={value}
        onChange={() => {}} // Empty function to make it read-only
      />
    );
  };

  const renderSkillImage = (imageUrl: string | undefined) => {
    if (!imageUrl) return null;
    
    return (
      <div className="skill-image-container">
        <img 
          src={imageUrl} 
          alt="Skill demonstration" 
          className="skill-image"
          onClick={() => setSelectedSkillImage(imageUrl)}
        />
      </div>
    );
  };

  const renderCommentsBox = (title: string, value: string | undefined) => {
    if (!value || value.trim() === '') return null;
    
    return (
      <CommentsBoxReadonly 
        title={title} 
        value={value}
      />
    );
  };

  // Check if a section has any content
  const hasSectionContent = (skills: any, section: string) => {
    if (!skills) return false;
    
    switch (section) {
      case 'top-flexibility':
        return (
          (skills.topflexscale && skills.topflexscale > 0) ||
          (skills.topflexheel && skills.topflexheel > 0) ||
          (skills.topflexarab && skills.topflexarab > 0) ||
          (skills.scorpion && skills.scorpion > 0) ||
          (skills.bowarrow && skills.bowarrow > 0) ||
          (skills.topflexcomments && skills.topflexcomments.trim() !== '') ||
          skills.topflexscaleimage || 
          skills.topflexheelimage || 
          skills.topflexarabimage || 
          skills.topflexscorpionimage || 
          skills.topflexbowarrowimage
        );
      
      case 'jumps':
        return (
          (skills.jumptoe && skills.jumptoe > 0) ||
          (skills.jumphurdle && skills.jumphurdle > 0) ||
          (skills.jumppike && skills.jumppike > 0) ||
          (skills.jumptoetuck && skills.jumptoetuck > 0) ||
          (skills.jumpcomments && skills.jumpcomments.trim() !== '')
        );
      
      case 'tumbling':
        return (
          (skills.tumblestandbhs && skills.tumblestandbhs > 0) ||
          (skills.tumblestandtuck && skills.tumblestandtuck > 0) ||
          (skills.tumblestand2bhstuck && skills.tumblestand2bhstuck > 0) ||
          (skills.tumblestand2bhslayout && skills.tumblestand2bhslayout > 0) ||
          (skills.tumblestand2bhsfull && skills.tumblestand2bhsfull > 0) ||
          (skills.tumblerunrotuck && skills.tumblerunrotuck > 0) ||
          (skills.tumblerunrolayout && skills.tumblerunrolayout > 0) ||
          (skills.tumblerunrofull && skills.tumblerunrofull > 0) ||
          (skills.tumbleruncombolayout && skills.tumbleruncombolayout > 0) ||
          (skills.tumbleruncombofull && skills.tumbleruncombofull > 0) ||
          (skills.tumblecomments && skills.tumblecomments.trim() !== '')
        );
      
      case 'stunt':
        return (
          (skills.stuntdoublebase && skills.stuntdoublebase > 0) ||
          (skills.stuntextension && skills.stuntextension > 0) ||
          (skills.stunttouch && skills.stunttouch > 0) ||
          (skills.stuntlib && skills.stuntlib > 0) ||
          (skills.stuntlibairposition && skills.stuntlibairposition > 0) ||
          (skills.stuntcradle && skills.stuntcradle > 0) ||
          (skills.stuntfrontpunch && skills.stuntfrontpunch > 0) ||
          (skills.stuntbacktuck && skills.stuntbacktuck > 0) ||
          (skills.stuntfulldown && skills.stuntfulldown > 0) ||
          (skills.stuntdoubledown && skills.stuntdoubledown > 0) ||
          (skills.stuntswitchup && skills.stuntswitchup > 0) ||
          (skills.stunthandsprintup && skills.stunthandsprintup > 0) ||
          (skills.stunthightohigh && skills.stunthightohigh > 0) ||
          (skills.stuntrewind && skills.stuntrewind > 0) ||
          (skills.stuntcomments && skills.stuntcomments.trim() !== '')
        );
        case 'basket':
          return (
            (skills.basketstraight && skills.basketstraight > 0) ||
            (skills.basketbacktuck && skills.basketbacktuck > 0) ||
            (skills.basketxout && skills.basketxout > 0) ||
            (skills.basketfull && skills.basketfull > 0) ||
            (skills.basketcomments && skills.basketcomments.trim() !== '')
          );
      
      case 'dance':
        return (
          (skills.dancemotions && skills.dancemotions > 0) ||
          (skills.danceperformance && skills.danceperformance > 0) ||
          (skills.dancecomments && skills.dancecomments.trim() !== '')
        );
      
      default:
        return false;
    }
  };

  const updateTeamPlacement = async (placement: string) => {
    if (!athlete || !sessionId || updating) return;

    try {
      setUpdating(true);
      const athleteRef = doc(db, `sessions/${sessionId}/athletes/${athlete.id}`);
      await updateDoc(athleteRef, {
        teamplacement: placement
      });
      // Update the local athlete object
      athlete.teamplacement = placement;
    } catch (error) {
      console.error('Error updating team placement:', error);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="new-athlete-page">
        <div className="new-athlete-top-bar">
          <div className="new-athlete-top-bar-left">
            <button className="new-athlete-top-bar-button" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
          <div className="new-athlete-top-bar-right">
            {/* <button className="new-athlete-top-bar-button" onClick={handleRefresh}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 3.81 7.58 3.81 12C3.81 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="currentColor"/>
              </svg>
            </button> */}
          </div>
        </div>
        <div className="new-athlete-content">
          <Container>
            <Card>
              <Typography variant="h2">Athlete Results</Typography>
              
              <div className="athlete-details">
                <div className="athlete-header">
                  <div className="athlete-image-large">
                    <div className="shimmer shimmer-photo"></div>
                  </div>
                  
                  <div className="athlete-info-detailed">
                    <div className="shimmer shimmer-text short"></div>
                    <div className="shimmer shimmer-text medium"></div>
                    <div className="shimmer shimmer-text short"></div>
                  </div>
                </div>
                
                <div className="actions">
                  <div className="shimmer shimmer-text long"></div>
                  <div className="shimmer shimmer-text long"></div>
                </div>
              </div>
            </Card>
          </Container>
        </div>
      </div>
    );
  }

  if (error || !athlete) {
    return (
      <div className="new-athlete-page">
        <div className="new-athlete-top-bar">
          <div className="new-athlete-top-bar-left">
            <button className="new-athlete-top-bar-button" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor" />
              </svg>
            </button>
          </div>
          <div className="new-athlete-top-bar-right">
            {/* <button className="new-athlete-top-bar-button" onClick={handleRefresh}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 3.81 7.58 3.81 12C3.81 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="currentColor" />
              </svg>
            </button> */}
          </div>
        </div>
        <div className="new-athlete-content">
          <Container>
            <Card>
              <Typography variant="h2" align="center">Error</Typography>
              <div className="error-message">{error}</div>
              <Button
                variant="primary"
                fullWidth
                onClick={onClose}
              >
                Back to Results
              </Button>
            </Card>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="new-athlete-page">
      <div className="new-athlete-top-bar">
        <div className="new-athlete-top-bar-left">
          <button className="new-athlete-top-bar-button" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor" />
            </svg>
          </button>
        </div>
        <div className="new-athlete-top-bar-right">
          {/* <button className="new-athlete-top-bar-button" onClick={handleRefresh}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 3.81 7.58 3.81 12C3.81 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="currentColor" />
            </svg>
          </button> */}
        </div>
      </div>
      
      <div className="new-athlete-content" ref={cardRef}>
        <Container>
          <Card>            
            {selectedSkillImage && (
              <div className="athlete-details-fullimage-overlay" onClick={() => setSelectedSkillImage(null)}>
                <div className="athlete-details-fullimage-container">
                  <img 
                    src={selectedSkillImage} 
                    alt="Skill demonstration"
                    onLoad={() => setFullImageLoaded(true)}
                    style={{
                      opacity: fullImageLoaded ? 1 : 0,
                      transition: 'opacity 0.4s ease',
                    }}
                  />
                  <Button 
                    variant="secondary" 
                    className="athlete-details-close-button"
                    onClick={() => setSelectedSkillImage(null)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
            
            <div className="athlete-details-container">
              <div className="card-header">
                
                {showFullImage && athlete?.imageUrl && (
                  <div className="athlete-details-fullimage-overlay" onClick={() => setShowFullImage(false)}>
                    <div className="athlete-details-fullimage-container">
                      <img 
                        src={athlete.imageUrl} 
                        alt={athlete.name}
                        onLoad={() => setFullImageLoaded(true)}
                        style={{
                          opacity: fullImageLoaded ? 1 : 0,
                          transition: 'opacity 0.4s ease',
                        }}
                      />
                      <Button 
                        variant="secondary" 
                        className="athlete-details-close-button"
                        onClick={() => setShowFullImage(false)}
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* <div className="athlete-details-info">
                  <div className="athlete-details-main">
                    <Typography variant="h3">#{athlete?.number}</Typography>
                    <Typography variant="h3">{athlete?.name}</Typography>
                  </div>
                  <Typography variant="body" className="athlete-details-pill">
                    {athlete?.position}
                  </Typography>
                  <div className="athlete-details-coach">
                    <Typography variant="body">Coach: {athlete?.createdBy}</Typography>
                  </div>
                </div> */}
                <Typography variant="h3">Rank Athlete</Typography>
                <div className="card-header-right">
                  {athlete?.returningstatus != null &&
                    <Typography variant="h3" className="athlete-details-pill">
                      {athlete?.returningstatus}
                    </Typography>
                  }

                  <Typography variant="h3" className="athlete-details-pill">
                    {athlete?.position}
                  </Typography>
                </div>
              </div>

              <div className="athlete-details-header">
                  <div className="athlete-details-photo" onClick={() => athlete?.imageUrl && setShowFullImage(true)}>
                    {(athlete?.imageThumbnailUrl || athlete?.imageUrl) ? (
                      <img
                        src={athlete.imageThumbnailUrl ?? athlete.imageUrl}
                        alt={athlete.name}
                        onLoad={() => setFullImageLoaded(true)}
                        style={{
                          opacity: fullImageLoaded ? 1 : 0,
                          transform: fullImageLoaded ? 'scale(1)' : 'scale(0.9)',
                          transition: 'opacity 0.4s ease'
                        }}
                      />
                    ) : (
                      <div className="placeholder-image">
                        <span>{athlete?.name.charAt(0)}</span>
                      </div>
                    )}
                  </div>

                  <div className="athlete-details-info">
                    <div className="athlete-details-main">
                      <div className="athlete-details-num">
                        <Typography variant="h3">#{athlete?.number}</Typography>
                      </div>
                      <div className="athlete-details-name">
                        <Typography variant="h3">{athlete?.name}</Typography>
                      </div>
                    </div>
                    <div className="athlete-details-coach">
                      <Typography variant="body">Coach: {athlete?.createdBy}</Typography>
                    </div>
                  </div>
                </div>

              
              <div className="athlete-details-content-final-result">
                {/* Top Flexibility Section */}
                {hasSectionContent(athlete.skills, 'top-flexibility') && (
                  <>
                    <div className="section-divider">
                      <Typography variant="h3">Top Flexibility</Typography>
                    </div>
                    <div className="detail-content">
                      <div className="skill-with-image">
                        {renderSkillJudge("Scale", athlete.skills?.topflexscale)}
                        {renderSkillImage(athlete.skills?.topflexscaleimage)}
                      </div>
                      
                      <div className="skill-with-image">
                        {renderSkillJudge("Heel Stretch", athlete.skills?.topflexheel)}
                        {renderSkillImage(athlete.skills?.topflexheelimage)}
                      </div>
                      
                      <div className="skill-with-image">
                        {renderSkillJudge("Arabesque", athlete.skills?.topflexarab)}
                        {renderSkillImage(athlete.skills?.topflexarabimage)}
                      </div>
                      
                      <div className="skill-with-image">
                        {renderSkillJudge("Scorpion", athlete.skills?.scorpion)}
                        {renderSkillImage(athlete.skills?.topflexscorpionimage)}
                      </div>
                      
                      <div className="skill-with-image">
                        {renderSkillJudge("Bow & Arrow", athlete.skills?.bowarrow)}
                        {renderSkillImage(athlete.skills?.topflexbowarrowimage)}
                      </div>
                      
                      {renderCommentsBox("Comments", athlete.skills?.topflexcomments)}
                    </div>
                  </>
                )}
                
                {/* Jumps Section */}
                {hasSectionContent(athlete.skills, 'jumps') && (
                  <>
                    <div className="section-divider">
                      <Typography variant="h3">Jumps</Typography>
                    </div>
                    <div className="detail-content">
                      {renderSkillJudge("Toe Touch", athlete.skills?.jumptoe)}
                      {renderSkillJudge("Hurdler", athlete.skills?.jumphurdle)}
                      {renderSkillJudge("Pike", athlete.skills?.jumppike)}
                      {renderSkillJudge("Toe Touch Tuck", athlete.skills?.jumptoetuck)}
                      {renderCommentsBox("Comments", athlete.skills?.jumpcomments)}
                    </div>
                  </>
                )}
                
                {/* Tumbling Section */}
                {hasSectionContent(athlete.skills, 'tumbling') && (
                  <>
                    <div className="section-divider">
                      <Typography variant="h3">Tumbling</Typography>
                    </div>
                    <div className="detail-content">
                      <div className="section-divider">
                        <Typography variant="h4">Standing</Typography>
                      </div>
                      
                      {renderSkillJudge("BHS", athlete.skills?.tumblestandbhs)}
                      {renderSkillJudge("Tuck", athlete.skills?.tumblestandtuck)}
                      {renderSkillJudge("2x BHS Tuck", athlete.skills?.tumblestand2bhstuck)}
                      {renderSkillJudge("2x BHS Layout", athlete.skills?.tumblestand2bhslayout)}
                      {renderSkillJudge("2x BHS Full", athlete.skills?.tumblestand2bhsfull)}
                      
                      <div className="section-divider">
                        <Typography variant="h4">Running</Typography>
                      </div>
                      
                      {renderSkillJudge("RO or RO BHS Tuck", athlete.skills?.tumblerunrotuck)}
                      {renderSkillJudge("RO or RO BHS Layout", athlete.skills?.tumblerunrolayout)}
                      {renderSkillJudge("RO or RO BHS Full", athlete.skills?.tumblerunrofull)}
                      {renderSkillJudge("Combo Layout", athlete.skills?.tumbleruncombolayout)}
                      {renderSkillJudge("Combo Full", athlete.skills?.tumbleruncombofull)}
                      
                      {renderCommentsBox("Comments", athlete.skills?.tumblecomments)}
                    </div>
                  </>
                )}
                
                {/* Stunt Section */}
                {hasSectionContent(athlete.skills, 'stunt') && (
                  <>
                    <div className="section-divider">
                      <Typography variant="h3">Stunt</Typography>
                    </div>
                    <div className="detail-content">
                      <div className="section-divider">
                        <Typography variant="h4">Static</Typography>
                      </div>
                      
                      {renderSkillJudge("Double Base", athlete.skills?.stuntdoublebase)}
                      {renderSkillJudge("Extension", athlete.skills?.stuntextension)}
                      {renderSkillJudge("Torch", athlete.skills?.stunttouch)}
                      {renderSkillJudge("Lib", athlete.skills?.stuntlib)}
                      {renderSkillJudge("Lib w/ Air Position", athlete.skills?.stuntlibairposition)}
                      
                      <div className="section-divider">
                        <Typography variant="h4">Dismount</Typography>
                      </div>
                      
                      {renderSkillJudge("Cradle", athlete.skills?.stuntcradle)}
                      {renderSkillJudge("Front Punch", athlete.skills?.stuntfrontpunch)}
                      {renderSkillJudge("Back Tuck", athlete.skills?.stuntbacktuck)}
                      {renderSkillJudge("Full Down", athlete.skills?.stuntfulldown)}
                      {renderSkillJudge("Double Down", athlete.skills?.stuntdoubledown)}
                      
                      <div className="section-divider">
                        <Typography variant="h4">Specialty</Typography>
                      </div>
                      
                      {renderSkillJudge("Switch Up", athlete.skills?.stuntswitchup)}
                      {renderSkillJudge("Handspring Up", athlete.skills?.stunthandsprintup)}
                      {renderSkillJudge("High-To-High", athlete.skills?.stunthightohigh)}
                      {renderSkillJudge("Rewind", athlete.skills?.stuntrewind)}
                      
                      {renderCommentsBox("Comments", athlete.skills?.stuntcomments)}
                    </div>
                  </>
                )}

                {/* Basket Section */}
                {hasSectionContent(athlete.skills, 'basket') && (
                  <>
                    <div className="section-divider">
                      <Typography variant="h3">Basket</Typography>
                    </div>
                    <div className="detail-content">
                      {renderSkillJudge("Staight Ride", athlete.skills?.basketstraight)}
                      {renderSkillJudge("Back Tuck", athlete.skills?.basketbacktuck)}
                      {renderSkillJudge("X Out", athlete.skills?.basketxout)}
                      {renderSkillJudge("Full", athlete.skills?.basketfull)}
                     
                      {renderCommentsBox("Comments", athlete.skills?.basketcomments)}
                    </div>
                  </>
                )}
                
                {/* Dance Section */}
                {hasSectionContent(athlete.skills, 'dance') && (
                  <>
                    <div className="section-divider">
                      <Typography variant="h3">Dance</Typography>
                    </div>
                    <div className="detail-content">
                      {renderSkillJudge("Motions", athlete.skills?.dancemotions)}
                      {renderSkillJudge("Performance", athlete.skills?.danceperformance)}
                      {renderCommentsBox("Comments", athlete.skills?.dancecomments)}
                    </div>
                  </>
                )}
                
                {/* Comments Section */}
                {athlete.commentList && athlete.commentList.length > 0 && (
                  <>
                    <div className="section-divider">
                      <Typography variant="h3">Comments</Typography>
                    </div>
                    <div className="detail-content">
                      {athlete.commentList.map((comment, index) => (
                        <CommentsBoxReadonly 
                          key={index}
                          title={`Comment ${index + 1}`} 
                          value={comment}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </Card>
        </Container>
      </div>
      
      {/* Footer with team placement and navigation buttons */}
      <div className="athlete-detail-final-result-footer">
        <div className="team-placement-buttons" >
          <Button
            variant={athlete.teamplacement === 'coed' ? 'primary' : 'secondary'}
            onClick={() => updateTeamPlacement('coed')}
            disabled={updating}
          >
            COED
          </Button>
          <Button
            variant={athlete.teamplacement === 'allgirl' ? 'primary' : 'secondary'}
            onClick={() => updateTeamPlacement('allgirl')}
            disabled={updating}
          >
            AG
          </Button>
          <Button
            variant={athlete.teamplacement === 'level4' ? 'primary' : 'secondary'}
            onClick={() => updateTeamPlacement('level4')}
            disabled={updating}
          >
            L4
          </Button>
          <Button
            variant={athlete.teamplacement === 'yes' ? 'primary' : 'secondary'}
            onClick={() => updateTeamPlacement('yes')}
            disabled={updating}
          >
            Yes?
          </Button>
          <Button
            variant={athlete.teamplacement === 'maybe' ? 'primary' : 'secondary'}
            onClick={() => updateTeamPlacement('maybe')}
            disabled={updating}
          >
            Maybe
          </Button>
          <Button
            variant={athlete.teamplacement === 'no' ? 'primary' : 'secondary'}
            onClick={() => updateTeamPlacement('no')}
            disabled={updating}
          >
            No
          </Button>
        </div>
        
        <div className="navigation-buttons" style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
          {hasPrevious && (
            <Button 
              variant="secondary" 
              onClick={handlePrevious}
            >
              ⬅️ Previous
            </Button>
          )}
          
          {hasNext && (
            <Button 
              variant="secondary" 
              onClick={handleNext}
            >
              Next ➡️
            </Button>
          )}
        </div>
      </div>
    </div>
  );
} 